import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { actorLabel, classifyActor, roleLabel } from './actor';
import { useActors } from './useActors';
import './AuditInfo.scss';
import { formatDateTime } from '@helpers/date';

interface AuditInfoProps {
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  className?: string;
}

const stamp = (at: string) => formatDateTime(at);

/**
 * One "by" line: who, and what they are.
 *
 * These fields hold a raw identifier, and this component printed it verbatim -- so in production
 * every workspace showed a 24-character ObjectId where a name belonged. 94% of those ids resolve
 * to a real account (mostly employers), and the rest of the column is emails and system labels
 * that never needed a lookup at all.
 *
 * Renders the shortened id immediately and upgrades to the name when the batch resolves, rather
 * than holding the line blank: the id is what it showed before, so the worst case is no worse.
 */
const ActorLine: React.FC<{ value: string; resolved: ReturnType<typeof useActors> }> = ({
  value,
  resolved,
}) => {
  const classified = classifyActor(value);
  if (!classified) return null;
  const actor = classified.lookupId ? resolved.get(classified.lookupId) : undefined;
  const role = roleLabel(actor?.role);
  return (
    <Box className="audit-user-row">
      <PersonOutlineIcon className="audit-icon audit-icon--user" fontSize="small" />
      <Typography className="audit-user" variant="caption">
        {actorLabel(classified, actor)}
        {role && <span className="audit-user__role">{role}</span>}
      </Typography>
    </Box>
  );
};

/**
 * Created / last-updated for a record.
 *
 * Each half renders only when the record actually carries it. Not every API
 * returns all four — the internal-users endpoint has no updatedAt or
 * updatedBy — and the old version always drew both, so those records showed a
 * "Last Updated" heading over two dashes and a "Created by Unknown". A
 * labelled blank is worse than no label.
 */
const AuditInfo: React.FC<AuditInfoProps> = ({
  createdAt,
  createdBy,
  updatedAt,
  updatedBy,
  className = ""
}) => {
  // Both ids in one request: the same account usually wrote both.
  const actors = useActors([createdBy, updatedBy]);

  // After the hook, never before it -- an early return above a hook is React error #310, which
  // this console has already shipped once.
  if (!createdAt && !updatedAt) return null;

  return (
    <Box
      className={`audit-info ${className}`}
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, width: '100%' }}
    >
      {createdAt && (
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Box className="audit-field">
            <Typography className="audit-label" variant="caption">
              Created
            </Typography>
            <Box className="audit-value-row">
              <ScheduleOutlinedIcon className="audit-icon" fontSize="small" />
              <Typography className="audit-value" variant="body2">
                {stamp(createdAt)}
              </Typography>
            </Box>
            {createdBy && <ActorLine value={createdBy} resolved={actors} />}
          </Box>
        </Box>
      )}

      {updatedAt && (
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Box className="audit-field">
            <Typography className="audit-label" variant="caption">
              Last Updated
            </Typography>
            <Box className="audit-value-row">
              <ScheduleOutlinedIcon className="audit-icon" fontSize="small" />
              <Typography className="audit-value" variant="body2">
                {stamp(updatedAt)}
              </Typography>
            </Box>
            {updatedBy && <ActorLine value={updatedBy} resolved={actors} />}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AuditInfo;
